import {
    EntityType,
    LinkedContentObjectsContract,
    TrackedRideContract,
} from '@polaris-customerweb/sharedcomponents.core';
import {
    GeoBounds,
    GeoCoordinate,
} from '@polaris-customerweb/sharedcomponents.shared';
import {
    Geofence,
    PlannedRide,
    TrackedRide,
    UserContentItem,
    Waypoint,
} from '@polaris-customerweb/sharedcomponents.user-content';
import { cloneDeep, flatten } from 'lodash-es';
import { Observable } from 'rxjs';
import { Collection } from './collection';
import { CommunityItem } from './community-item';
import { LinkedItem } from './linked-item';
import { RideEvent } from './ride-event';

export class ContentCollection {
    private _allGeofences?: readonly Geofence[];
    private _allPlannedRides?: readonly PlannedRide[];
    private _allTrackedRides?: readonly TrackedRide[];
    private _allWaypoints?: readonly Waypoint[];
    private _bounds?: GeoBounds;
    private _collections?: readonly Collection[];
    private _communityItems: readonly CommunityItem[];
    private _contentObservableFactory?: (
        id: string
    ) => Observable<ContentCollection>;
    private _geofences: readonly Geofence[];
    private _isSno?: boolean;
    private _plannedRides: readonly PlannedRide[];
    private _rideEvents: readonly RideEvent[];
    private _totalDistanceInMeters?: number;
    private _totalDurationInSeconds?: number;
    private _trackedRides: readonly TrackedRide[];
    private _waypoints: readonly Waypoint[];
    private rawContentCollection: LinkedContentObjectsContract;

    /**
     * @description ContentCollection
     * @param rawContentCollection Content collection contract.
     * @param contentObservableFactory Function that will create a ContentCollection Observable for each child
     * collection.
     */
    constructor(
        rawContentCollection?: LinkedContentObjectsContract,
        contentObservableFactory?: (id: string) => Observable<ContentCollection>
    ) {
        if (rawContentCollection) {
            this.rawContentCollection = cloneDeep(rawContentCollection);
        } else {
            this.rawContentCollection = {};
        }
        this._contentObservableFactory = contentObservableFactory;
    }

    get allGeofences(): readonly Geofence[] {
        if (!this._allGeofences) {
            const collectionGeofences = flatten(
                this.collections.map((collection) => {
                    return collection.content?.allGeofences || [];
                })
            );
            const rideEventGeofences = flatten(
                this.rideEvents.map((rideEvent) => {
                    return rideEvent.allGeofences || [];
                })
            );
            const communityGeofences = this.communityItems.reduce(
                (geofences, communityItem) => {
                    if (communityItem.linkedType === 'Event') {
                        return [
                            ...geofences,
                            ...communityItem.content.event.allGeofences,
                        ];
                    } else if (communityItem.linkedType === 'Collection') {
                        return [
                            ...geofences,
                            ...(communityItem.content.collection.content
                                ?.allGeofences || []),
                        ];
                    } else if (communityItem.linkedType === 'Geofence') {
                        return [...geofences, communityItem.content.geofence];
                    } else {
                        return geofences;
                    }
                },
                []
            );

            this._allGeofences = [
                ...this.geofences,
                ...collectionGeofences,
                ...rideEventGeofences,
                ...communityGeofences,
            ];
        }

        return this._allGeofences;
    }

    get allPlannedRides(): readonly PlannedRide[] {
        if (!this._allPlannedRides) {
            const collectionPlannedRides = flatten(
                this.collections.map((collection) => {
                    return collection.content?.allPlannedRides || [];
                })
            );
            const rideEventPlannedRides = flatten(
                this.rideEvents.map((rideEvent) => {
                    return rideEvent.allPlannedRides || [];
                })
            );
            const communityPlannedRides = this.communityItems.reduce(
                (plannedRides, communityItem) => {
                    if (communityItem.linkedType === 'Event') {
                        return [
                            ...plannedRides,
                            ...communityItem.content.event.allPlannedRides,
                        ];
                    } else if (communityItem.linkedType === 'Collection') {
                        return [
                            ...plannedRides,
                            ...(communityItem.content.collection.content
                                ?.allPlannedRides || []),
                        ];
                    } else if (communityItem.linkedType === 'PlannedRide') {
                        return [
                            ...plannedRides,
                            communityItem.content.plannedRide,
                        ];
                    } else {
                        return plannedRides;
                    }
                },
                []
            );

            this._allPlannedRides = [
                ...this.plannedRides,
                ...collectionPlannedRides,
                ...rideEventPlannedRides,
                ...communityPlannedRides,
            ];
        }

        return this._allPlannedRides;
    }

    get allTrackedRides(): readonly TrackedRide[] {
        if (!this._allTrackedRides) {
            const collectionTrackedRides = flatten(
                this.collections.map((collection) => {
                    return collection.content?.allTrackedRides || [];
                })
            );
            const rideEventTrackedRides = flatten(
                this.rideEvents.map((rideEvent) => {
                    return rideEvent.allTrackedRides || [];
                })
            );
            const communityTrackedRides = this.communityItems.reduce(
                (trackedRides, communityItem) => {
                    if (communityItem.linkedType === 'Event') {
                        return [
                            ...trackedRides,
                            ...communityItem.content.event.allTrackedRides,
                        ];
                    } else if (communityItem.linkedType === 'Collection') {
                        return [
                            ...trackedRides,
                            ...(communityItem.content.collection.content
                                ?.allTrackedRides || []),
                        ];
                    } else if (communityItem.linkedType === 'TrackedRide') {
                        return [
                            ...trackedRides,
                            communityItem.content.trackedRide,
                        ];
                    } else {
                        return trackedRides;
                    }
                },
                []
            );

            this._allTrackedRides = [
                ...this.trackedRides,
                ...collectionTrackedRides,
                ...rideEventTrackedRides,
                ...communityTrackedRides,
            ];
        }

        return this._allTrackedRides;
    }
    get allWaypoints(): readonly Waypoint[] {
        if (!this._allWaypoints) {
            const collectionWaypoints = flatten(
                this.collections.map((collection) => {
                    return collection.content?.allWaypoints || [];
                })
            );
            const rideEventWaypoints = flatten(
                this.rideEvents.map((rideEvent) => {
                    return rideEvent.allWaypoints;
                })
            );
            const communityWaypoints = this.communityItems.reduce(
                (waypoints, communityItem) => {
                    if (communityItem.linkedType === 'Event') {
                        return [
                            ...waypoints,
                            ...communityItem.content.event.allWaypoints,
                        ];
                    } else if (communityItem.linkedType === 'Collection') {
                        return [
                            ...waypoints,
                            ...(communityItem.content.collection.allWaypoints ||
                                []),
                        ];
                    } else if (communityItem.linkedType === 'Waypoint') {
                        return [...waypoints, communityItem.content.waypoint];
                    } else {
                        return waypoints;
                    }
                },
                []
            );

            this._allWaypoints = [
                ...this.waypoints,
                ...collectionWaypoints,
                ...rideEventWaypoints,
                ...communityWaypoints,
            ];
        }

        return this._allWaypoints;
    }

    get bounds(): GeoBounds | undefined {
        if (!this._bounds) {
            this._bounds = this.calculateBounds();
        }

        return this._bounds;
    }
    set bounds(bounds: GeoBounds | undefined) {
        this._bounds = bounds;
    }

    get collections(): readonly Collection[] {
        if (!this._collections) {
            this._collections = this.rawContentCollection.collections?.map(
                (collection) => {
                    let contentObservable$ =
                        new Observable<ContentCollection>();
                    if (this._contentObservableFactory) {
                        contentObservable$ = this._contentObservableFactory(
                            collection.id || ''
                        );
                    }
                    return new Collection(collection, contentObservable$);
                }
            );
        }
        return this._collections || [];
    }
    set collections(collections: readonly Collection[]) {
        this._collections = collections;
        this.rawContentCollection.collections = collections.map((c) =>
            c.toContract()
        );
        this._allGeofences = undefined;
        this._allPlannedRides = undefined;
        this._allTrackedRides = undefined;
        this._allWaypoints = undefined;
        this._bounds = undefined;
        this._totalDistanceInMeters = undefined;
        this._totalDurationInSeconds = undefined;
        this._isSno = undefined;
    }

    get communityItems(): readonly CommunityItem[] {
        if (!this._communityItems) {
            this._communityItems =
                this.rawContentCollection.communityItems?.map(
                    (i) => new CommunityItem(i)
                ) || [];
        }
        return this._communityItems || [];
    }
    set communityItems(communityItems: readonly CommunityItem[]) {
        this._communityItems = communityItems;
        this.rawContentCollection.communityItems = communityItems.map((c) =>
            c.toContract()
        );
        this._allGeofences = undefined;
        this._allPlannedRides = undefined;
        this._allTrackedRides = undefined;
        this._allWaypoints = undefined;
        this._bounds = undefined;
        this._totalDistanceInMeters = undefined;
        this._totalDurationInSeconds = undefined;
        this._isSno = undefined;
    }

    get geofences(): readonly Geofence[] {
        if (!this._geofences) {
            this._geofences =
                this.rawContentCollection.geofences?.map(
                    (i) => new Geofence(i)
                ) || [];
        }
        return this._geofences || [];
    }
    set geofences(geofences: readonly Geofence[]) {
        this._geofences = geofences;
        this.rawContentCollection.geofences = geofences.map((g) =>
            g.toContract()
        );
        this._allGeofences = undefined;
        this._bounds = undefined;
    }

    get hasItems(): boolean {
        if (
            this.geofences.length > 0 ||
            this.plannedRides.length > 0 ||
            this.trackedRides.length > 0 ||
            this.waypoints.length > 0 ||
            this.communityItems.length > 0
        ) {
            return true;
        }

        for (const collection of this.collections) {
            if (collection.hasItems) {
                return true;
            }
        }

        for (const rideEvent of this.rideEvents) {
            if (rideEvent.hasItems) {
                return true;
            }
        }

        return false;
    }

    get isOrv(): boolean {
        return !this.isSno;
    }

    get isSno(): boolean {
        if (this._isSno === undefined) {
            this._isSno = this.getIsSno();
        }

        return this._isSno;
    }

    get plannedRides(): readonly PlannedRide[] {
        if (!this._plannedRides) {
            this._plannedRides =
                this.rawContentCollection.plannedRides?.map(
                    (i) => new PlannedRide(i)
                ) || [];
        }
        return this._plannedRides || [];
    }
    set plannedRides(plannedRides: readonly PlannedRide[]) {
        this._plannedRides = plannedRides;
        this.rawContentCollection.plannedRides = plannedRides.map((p) =>
            p.toContract()
        );
        this._allPlannedRides = undefined;
        this._bounds = undefined;
        this._totalDistanceInMeters = undefined;
        this._totalDurationInSeconds = undefined;
        this._isSno = undefined;
    }

    get rideEvents(): readonly RideEvent[] {
        if (!this._rideEvents) {
            this._rideEvents =
                this.rawContentCollection.events?.map(
                    (i) => new RideEvent(i)
                ) || [];
        }
        return this._rideEvents || [];
    }
    set rideEvents(rideEvents: readonly RideEvent[]) {
        this._rideEvents = rideEvents;
        this.rawContentCollection.events = rideEvents.map((e) =>
            e.toContract()
        );
        this._allGeofences = undefined;
        this._allPlannedRides = undefined;
        this._allTrackedRides = undefined;
        this._allWaypoints = undefined;
        this._bounds = undefined;
        this._totalDistanceInMeters = undefined;
        this._totalDurationInSeconds = undefined;
        this._isSno = undefined;
    }

    get totalDistanceInMeters(): number {
        if (!this._totalDistanceInMeters) {
            this._totalDistanceInMeters = this.calculateTotalDistanceInMeters();
        }

        return this._totalDistanceInMeters;
    }

    get totalDurationInSeconds(): number {
        if (!this._totalDurationInSeconds) {
            this._totalDurationInSeconds =
                this.calculateTotalDurationInSeconds();
        }

        return this._totalDurationInSeconds;
    }

    get trackedRides(): readonly TrackedRide[] {
        if (!this._trackedRides) {
            this._trackedRides =
                this.rawContentCollection.trackedRides?.map(
                    (i) => new TrackedRide(i)
                ) || [];
        }
        return this._trackedRides || [];
    }
    set trackedRides(trackedRides: readonly TrackedRide[]) {
        this._trackedRides = trackedRides;
        this.rawContentCollection.trackedRides = trackedRides.map(
            (t) => t.toGeoJson() as TrackedRideContract
        );
        this._allTrackedRides = undefined;
        this._bounds = undefined;
        this._totalDistanceInMeters = undefined;
        this._totalDurationInSeconds = undefined;
        this._isSno = undefined;
    }

    get waypoints(): readonly Waypoint[] {
        if (!this._waypoints) {
            this._waypoints =
                this.rawContentCollection.waypoints?.map(
                    (i) => new Waypoint(i)
                ) || [];
        }
        return this._waypoints || [];
    }
    set waypoints(waypoints: readonly Waypoint[]) {
        this._waypoints = waypoints;
        this.rawContentCollection.waypoints = waypoints.map((w) =>
            w.toContract()
        );
        this._allWaypoints = undefined;
        this._bounds = undefined;
        this._isSno = undefined;
    }

    /**
     * @param geofences
     * @param plannedRides
     * @param trackedRides
     * @param waypoints
     * @param collections
     * @param rideEvents
     * @description Add content to the existing content collection. After calling this, be sure to update the
     * associated linkedItems list.
     */
    addUserContentArrays(
        geofences?: Geofence[],
        plannedRides?: PlannedRide[],
        trackedRides?: TrackedRide[],
        waypoints?: Waypoint[],
        collections?: Collection[],
        rideEvents?: RideEvent[]
    ): void {
        if (geofences?.length) {
            const filteredGeofences = geofences.filter(
                (geofence) =>
                    !this.geofences.some((fence) => fence.id === geofence.id)
            );
            this.geofences = [...this.geofences, ...filteredGeofences];
        }
        if (plannedRides?.length) {
            const filteredPlannedRides = plannedRides.filter(
                (plannedRide) =>
                    !this.plannedRides.some(
                        (ride) => ride.id === plannedRide.id
                    )
            );
            this.plannedRides = [...this.plannedRides, ...filteredPlannedRides];
        }
        if (trackedRides?.length) {
            const filteredTrackedRides = trackedRides.filter(
                (trackedRide) =>
                    !this.trackedRides.some(
                        (ride) => ride.id === trackedRide.id
                    )
            );
            this.trackedRides = [...this.trackedRides, ...filteredTrackedRides];
        }
        if (waypoints?.length) {
            const filteredWaypoints = waypoints.filter(
                (waypoint) =>
                    !this.waypoints.some((point) => point.id === waypoint.id)
            );
            this.waypoints = [...this.waypoints, ...filteredWaypoints];
        }
        if (collections?.length) {
            const filteredCollections = collections.filter(
                (collection) =>
                    !this.collections.some((item) => item.id === collection.id)
            );
            this.collections = [...this.collections, ...filteredCollections];
        }
        if (rideEvents?.length) {
            const filteredRideEvents = rideEvents.filter(
                (rideEvent) =>
                    !this.rideEvents.some((event) => event.id === rideEvent.id)
            );
            this.rideEvents = [...this.rideEvents, ...filteredRideEvents];
        }
    }

    clone(): ContentCollection {
        const contentCollection = new ContentCollection(this.toContract());
        contentCollection.bounds = this.bounds as GeoBounds;
        return contentCollection;
    }

    getCollection(collectionId: string): Collection | undefined {
        return this.collections.find(
            (collection) => collection.id === collectionId
        );
    }

    getCommunityItem(communityItemId: string): CommunityItem | undefined {
        return this.communityItems.find(
            (communityItem) => communityItem.id === communityItemId
        );
    }

    getGeofence(geofenceId: string): Geofence | undefined {
        return this.geofences.find((geofence) => geofence.id === geofenceId);
    }

    getPlannedRide(plannedRideId: string): PlannedRide | undefined {
        return this.plannedRides.find(
            (plannedRide) => plannedRide.id === plannedRideId
        );
    }

    getRideEvent(rideEventId: string): RideEvent | undefined {
        return this.rideEvents.find(
            (rideEvent) => rideEvent.id === rideEventId
        );
    }

    getTrackedRide(trackedRideId: string): TrackedRide | undefined {
        return this.trackedRides.find(
            (trackedRide) => trackedRide.id === trackedRideId
        );
    }

    getUserContentItem(
        id: string,
        type: EntityType
    ): UserContentItem | undefined {
        switch (type) {
            case 'Collection':
                return this.getCollection(id);
            case 'CommunityItem':
                return this.getCommunityItem(id);
            case 'Event':
                return this.getRideEvent(id);
            case 'Geofence':
                return this.getGeofence(id);
            case 'PlannedRide':
                return this.getPlannedRide(id);
            case 'TrackedRide':
                return this.getTrackedRide(id);
            case 'Waypoint':
                return this.getWaypoint(id);
            default:
                return undefined;
        }
    }

    getWaypoint(waypointId: string): Waypoint | undefined {
        return this.waypoints.find((waypoint) => waypoint.id === waypointId);
    }

    /**
     * @param id
     * @param entityType
     * @description Remove an item from the content collection. After calling this, be sure to update the associated
     * linkedItems list.
     */
    removeItem(id: string, entityType: EntityType): void {
        switch (entityType) {
            case 'Collection':
                this.collections = this.collections.filter(
                    (collection) => collection.id !== id
                );
                break;
            case 'CommunityItem':
                this.communityItems = this.communityItems.filter(
                    (communityItem) => communityItem.id !== id
                );
                break;
            case 'Event':
                this.rideEvents = this.rideEvents.filter(
                    (rideEvent) => rideEvent.id !== id
                );
                break;
            case 'Geofence':
                this.geofences = this.geofences.filter(
                    (geofence) => geofence.id !== id
                );
                break;
            case 'PlannedRide':
                this.plannedRides = this.plannedRides.filter(
                    (plannedRide) => plannedRide.id !== id
                );
                break;
            case 'TrackedRide':
                this.trackedRides = this.trackedRides.filter(
                    (trackedRide) => trackedRide.id !== id
                );
                break;
            case 'Waypoint':
                this.waypoints = this.waypoints.filter(
                    (waypoint) => waypoint.id !== id
                );
                break;
        }
    }

    toContract(): LinkedContentObjectsContract {
        return cloneDeep(this.rawContentCollection);
    }

    toLinkedItems(): LinkedItem[] {
        return [
            ...this.collections.map((collection) =>
                LinkedItem.fromUserContentItem(collection)
            ),
            ...this.communityItems.map((communityItem) =>
                LinkedItem.fromUserContentItem(communityItem)
            ),
            ...this.geofences.map((geofence) =>
                LinkedItem.fromUserContentItem(geofence)
            ),
            ...this.plannedRides.map((plannedRide) =>
                LinkedItem.fromUserContentItem(plannedRide)
            ),
            ...this.rideEvents.map((rideEvent) =>
                LinkedItem.fromUserContentItem(rideEvent)
            ),
            ...this.trackedRides.map((trackedRide) =>
                LinkedItem.fromUserContentItem(trackedRide)
            ),
            ...this.waypoints.map((waypoint) =>
                LinkedItem.fromUserContentItem(waypoint)
            ),
        ];
    }

    private calculateBounds(): GeoBounds | undefined {
        const geoCoordinates: (GeoCoordinate | GeoCoordinate[])[] = [
            ...this.allGeofences.map((geofence) => [
                geofence.bounds.northEast,
                geofence.bounds.southWest,
            ]),
            ...this.allPlannedRides.map((plannedRide) => [
                plannedRide.bounds.northEast,
                plannedRide.bounds.southWest,
            ]),
            ...this.allTrackedRides.map((trackedRide) => [
                trackedRide.bounds.northEast,
                trackedRide.bounds.southWest,
            ]),
            ...this.allWaypoints.map((waypoint) => [waypoint.geoCoordinate]),
        ];

        if (!geoCoordinates || geoCoordinates.length === 0) {
            return undefined;
        } else {
            return GeoBounds.fromGeoCoordinates(flatten(geoCoordinates));
        }
    }

    private calculateTotalDistanceInMeters(): number {
        const plannedRideDistance = this.allPlannedRides.reduce(
            (distance, plannedRide) => {
                return distance + plannedRide.ride.totalLengthInMeters;
            },
            0
        );
        const trackedRideDistance = this.allTrackedRides.reduce(
            (distance, trackedRide) => {
                return distance + trackedRide.totalLengthMeters;
            },
            0
        );

        return plannedRideDistance + trackedRideDistance;
    }

    private calculateTotalDurationInSeconds(): number {
        const plannedRideDuration = this.allPlannedRides.reduce(
            (duration, plannedRide) => {
                return duration + plannedRide.ride.totalDurationInSeconds;
            },
            0
        );
        const trackedRideDuration = this.allTrackedRides.reduce(
            (duration, trackedRide) => {
                return duration + trackedRide.totalDurationSeconds;
            },
            0
        );

        return plannedRideDuration + trackedRideDuration;
    }

    private getIsSno(): boolean {
        const plannedRideSnowCount = this.allPlannedRides.reduce(
            (snowCount, plannedRide) => {
                return (
                    snowCount +
                    (plannedRide.isSno && !plannedRide.isOrv ? 1 : -1)
                );
            },
            0
        );
        const trackedRideSnowCount = this.allTrackedRides.reduce(
            (snowCount, trackedRide) => {
                return (
                    snowCount +
                    (trackedRide.isSno && !trackedRide.isOrv ? 1 : -1)
                );
            },
            0
        );
        const waypointSnowCount = this.allWaypoints.reduce(
            (snowCount, waypoint) => {
                return snowCount + (waypoint.isSno && !waypoint.isOrv ? 1 : -1);
            },
            0
        );

        return (
            plannedRideSnowCount + trackedRideSnowCount + waypointSnowCount > 0
        );
    }
}
